import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  LoadingOverlay,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { ArticlesFilters, ArticlesList } from "../../entities/articles/ui";
import { ProcessArticle } from "../../features/process-article/view";
import { useGate, useUnit } from "effector-react";
import { useParams, useNavigate, useLocation } from "react-router";
import { paths } from "../../shared/configs/paths";

import { colorByStatus, CampaignStatus } from "../../entities/campaigns/types";
import { format, parseISO } from "date-fns";
import { CampaignsErrors } from "../../entities/campaigns/ui";
import styles from "./styles.module.scss";
import { campaignDetailsEntityModel } from "../../entities/campaigns/model/details";
import { IconFileTypeCsv, IconArrowLeft } from "@tabler/icons-react";

export const CampaignDetailsPage = () => {
  const params = useParams();
  const navigation = useNavigate();
  const location = useLocation();

  const { campaign, isLoading, exportToCsv } = useUnit({
    campaign: campaignDetailsEntityModel.$data,
    isLoading: campaignDetailsEntityModel.$isLoading,
    exportToCsv: campaignDetailsEntityModel.exportToCsv,
  });

  const statistics = campaign?.statistics;

  const shouldShowStatistics =
    campaign?.status === CampaignStatus.completed ||
    campaign?.status === CampaignStatus.failed;

  useGate(campaignDetailsEntityModel.Gate, { id: Number(params.id) });

  return (
    <Box p="md" pt="lg">
      <Flex justify="space-between" align="center">
        <Stack gap={0}>
          <Skeleton visible={isLoading}>
            <Group gap={"xs"} wrap="nowrap">
              <Tooltip label={campaign?.name ?? "N/A"} withArrow>
                <Title order={3} fw={500} lineClamp={3} maw={400}>
                  {campaign?.name ?? "N/A"}
                </Title>
              </Tooltip>

              <CampaignsErrors />
            </Group>
          </Skeleton>
        </Stack>

        <Group>
          <Button
            variant="default"
            size="md"
            leftSection={<IconArrowLeft />}
            onClick={() => location.key === 'default' ? navigation(paths.home) : navigation(-1) }
          >
            Back to Campaigns
          </Button>
          <Button
            disabled={
              isLoading || campaign?.status !== CampaignStatus.completed
            }
            variant="default"
            size="md"
            leftSection={<IconFileTypeCsv />}
            onClick={() => exportToCsv({ campaignId: Number(campaign?.id) })}
          >
            CSV
          </Button>
          <ProcessArticle />
        </Group>
      </Flex>

      <Paper p="md" radius="md" my="lg" className={styles.detailsPaper}>
        <Stack>
          <Group justify="space-between">
            <Group>
              <Text size="md" fw={500}>
                Details
              </Text>
            </Group>
          </Group>
          {!isLoading && (
            <ScrollArea>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Creator</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th>Tasks</Table.Th>
                    <Table.Th>Pending</Table.Th>
                    <Table.Th>Failed</Table.Th>
                    <Table.Th>Finished</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td maw={200}>
                      <Tooltip label={campaign?.category ?? "N/A"} withArrow>
                        <Text truncate>{campaign?.category ?? "N/A"}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>{campaign?.createdByEmail ?? "N/A"}</Table.Td>
                    <Table.Td>
                      {campaign?.createdAt &&
                        format(
                          parseISO(campaign?.createdAt ?? ""),
                          "dd/MM/yyyy HH:mm"
                        )}
                    </Table.Td>
                    <Table.Td>{statistics?.total ?? "N/A"}</Table.Td>
                    <Table.Td>{statistics?.pending ?? "N/A"}</Table.Td>
                    <Table.Td>{statistics?.failed ?? "N/A"}</Table.Td>
                    <Table.Td>{statistics?.completed ?? "N/A"}</Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          colorByStatus[
                          campaign?.status ?? CampaignStatus.pending
                          ]
                        }
                        variant="light"
                      >
                        {campaign?.status === CampaignStatus.completed &&
                          "Finished"}
                        {campaign?.status === CampaignStatus.pending &&
                          "Processing"}
                        {campaign?.status === CampaignStatus.failed && "Failed"}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Stack>
      </Paper>

      <Stack pos="relative" h="100%" my="lg" className={styles.statisticsPaper}>
        <Paper p="md" radius="md">
          <Stack>
            <Group justify="space-between">
              <Text size="md" fw={500}>
                Statistics
              </Text>
            </Group>
            <ScrollArea>
              <Stack pos="relative">
                <Table pos={"relative"}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Domains Count</Table.Th>
                      <Table.Th>Min Similarities</Table.Th>
                      <Table.Th>Avg Similarities</Table.Th>
                      <Table.Th>Max Similarities</Table.Th>
                      <Table.Th>Top Domains</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td>{statistics?.domainsCount ?? "N/A"}</Table.Td>
                      <Table.Td>{statistics?.minSimilarity ?? "N/A"}</Table.Td>
                      <Table.Td>{statistics?.avgSimilarity ?? "N/A"}</Table.Td>
                      <Table.Td>{statistics?.maxSimilarity ?? "N/A"}</Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={10}>
                          {statistics?.topDomains?.join(", ") ?? "N/A"},
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
                {!shouldShowStatistics && (
                  <Stack flex={1} mih={60}>
                    <Stack
                      mih={60}
                      pos={"absolute"}
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                    >
                      <LoadingOverlay
                        visible={!shouldShowStatistics}
                        zIndex={100}
                        overlayProps={{
                          radius: "sm",
                          blur: 5,
                          backgroundOpacity: 0.8,
                        }}
                        loaderProps={{ type: "bars" }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </ScrollArea>
            {!shouldShowStatistics && (
              <Text size="sm" color="dimmed" ta="center">
                The final report will be available after all tasks are completed
                campaign.
              </Text>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Paper p="md" radius="md" className={styles.articlesPaper}>
        <Text size="md" fw={500}>
          Articles
        </Text>

        <ArticlesFilters />

        <ArticlesList campaignId={Number(campaign?.id)} />
      </Paper>
    </Box>
  );
};
